import { Link } from '@inertiajs/react';
import { ArrowRight } from 'lucide-react';

export function CategoryRailSection({
    title,
    subtitle,
    categories = [],
    ctaHref = '/collections',
    ctaLabel = 'View All',
    accent = 'pink',
}) {
    const accentStyles =
        accent === 'rose'
            ? {
                  label: 'text-[#9f1239]',
                  link: 'bg-[#9f1239] shadow-[0_14px_30px_rgba(159,18,57,0.18)] hover:bg-[#7f0f2e]',
                  card:
                      'border-rose-100 bg-[linear-gradient(180deg,#fff7f9_0%,#fffdfd_100%)]',
                  badge: 'bg-rose-50 text-[#9f1239] ring-rose-100',
              }
            : {
                  label: 'text-[#d80b67]',
                  link: 'bg-[#f51b75] shadow-[0_14px_30px_rgba(245,27,117,0.22)] hover:bg-[#d80b67]',
                  card:
                      'border-rose-100 bg-[linear-gradient(180deg,#fff7fb_0%,#ffffff_100%)]',
                  badge: 'bg-[#fff0f5] text-[#d80b67] ring-rose-100',
              };

    return (
        <section className="px-4 sm:px-6 lg:px-10">
            <div className="mx-auto w-full max-w-[1440px] rounded-[2rem] border border-rose-100/70 bg-white/90 px-4 py-5 shadow-[0_18px_60px_rgba(236,72,153,0.06)] backdrop-blur-sm sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className={`text-sm font-semibold uppercase tracking-[0.3em] ${accentStyles.label}`}>
                            {subtitle}
                        </p>
                        <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">
                            {title}
                        </h2>
                    </div>

                    <Link
                        href={ctaHref}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 ${accentStyles.link}`}
                    >
                        {ctaLabel}
                        <ArrowRight className="size-4" />
                    </Link>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {categories.length ? (
                        categories.slice(0, 4).map((category) => (
                            <CategoryRailCard
                                key={category.id}
                                category={category}
                                accentStyles={accentStyles}
                            />
                        ))
                    ) : (
                        <EmptyCategoryRail accentStyles={accentStyles} />
                    )}
                </div>
            </div>
        </section>
    );
}

function CategoryRailCard({ category, accentStyles }) {
    return (
        <Link
            href={category.href}
            className={`group flex items-center gap-4 overflow-hidden rounded-[1.5rem] border p-3 transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_35px_rgba(236,72,153,0.08)] ${accentStyles.card}`}
        >
            <div className="relative size-20 shrink-0 overflow-hidden rounded-[1.25rem]">
                {category.image ? (
                    <img
                        src={category.image}
                        alt={category.title}
                        className="size-full object-cover transition duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="flex size-full items-center justify-center bg-[linear-gradient(180deg,#ffe7ef_0%,#f59ab7_100%)] text-[9px] font-black uppercase tracking-[0.25em] text-white">
                        Glow
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div
                    className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ring-1 ${accentStyles.badge}`}
                >
                    {category.productCount || 0} Products
                </div>
                <h3 className="mt-2 truncate text-base font-black text-slate-900">
                    {category.title}
                </h3>
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
                    {category.subtitle}
                </p>
            </div>

            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-slate-900 shadow-sm ring-1 ring-rose-100 transition group-hover:translate-x-0.5">
                <ArrowRight className="size-4" />
            </span>
        </Link>
    );
}

function EmptyCategoryRail({ accentStyles }) {
    return (
        <div className={`col-span-full rounded-[1.5rem] border border-dashed px-5 py-8 text-sm text-slate-500 ${accentStyles.card}`}>
            No categories available yet.
        </div>
    );
}
